import { Button, Checkbox } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import React, {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import getSetting, { getLogoUrl } from "../../api/getSettings";
import number2words from "../../utils/numberToWords";
import bookLogo from "./../../assets/images/bookLogo.png";
import "./style.css";
import camelize from "../../utils/toCamelCase";

const PrintToPdf = forwardRef(({ data, invoiceData, sortAlphabetically = false }, ref) => {
  // Calculate total quantity
  const totalQuantity = data?.purchaseInvoiceProduct?.reduce(
    (sum, item) => sum + item.product_quantity,
    0
  );

  // Calculate gross total (before any discounts)
  const grossTotal = data?.purchaseInvoiceProduct?.reduce(
    (sum, item) => sum + (item.product_quantity * item.product_purchase_price),
    0
  );

  // Calculate total product discount amount
  const totalProductDiscountAmount = data?.purchaseInvoiceProduct?.reduce(
    (sum, item) => {
      const itemTotal = item.product_quantity * item.product_purchase_price;
      const itemDiscount = (itemTotal * (item.product_purchase_discount || 0)) / 100;
      return sum + itemDiscount;
    },
    0
  );

  // Calculate amounts for display
  const subtotalAfterProductDiscount = grossTotal - totalProductDiscountAmount;
  const finalTotal = data?.total_amount || 0;
  const additionalDiscount = data?.discount || 0;
  const roundOffAmount = data?.round_off_amount || 0;
  const paidAmount = data?.paid_amount || 0;
  const dueAmount = data?.due_amount || 0;

  return (
    <Fragment>
      <div
        ref={ref}
        className="wrapper"
        data-invoice-number={`${data?.prefix || "PINV/25-26/"}${data?.invoice_number ?? data?.id}`}
        data-invoice-date={moment(data?.date).format("DD-MMM-YYYY")}
        style={{ position: 'relative', paddingBottom: '24px' }}
      >
        <div className="invoice-top-meta">
          <span className="invoice-top-meta-left">
            Invoice No: {(data?.prefix || "PINV/25-26/")}{(data?.invoice_number ?? data?.id)}
          </span>
          <span className="invoice-top-meta-right">
            Invoice Date: {moment(data?.date).format("DD-MMM-YYYY")}
          </span>
        </div>
        {/* Company Detail - match Sale PDF header */}
        <div
          style={{
            gridColumn: "1/4",
            gridRow: "1",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "15px 20px",
            borderBottom: "2px solid #003a8c",
            marginBottom: "15px",
          }}
        >
          {/* Left - Logo */}
          <div style={{ flex: "0 0 auto", marginRight: "20px" }}>
            <img
              src={getLogoUrl(invoiceData?.logo) || bookLogo}
              style={{ maxHeight: "80px", width: "auto" }}
              alt="Company Logo"
            />
          </div>

          {/* Center - Company Info */}
          <div style={{ flex: "1", textAlign: "center", padding: "0 20px" }}>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: "24px",
                fontWeight: "bold",
                color: "#1a1a1a",
                letterSpacing: "1px",
              }}
            >
              {invoiceData?.company_name}
            </h2>
            <div style={{ fontSize: "11px", color: "#666", lineHeight: "1.6" }}>
              <div style={{ marginBottom: "4px" }}>{invoiceData?.address}</div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "15px",
                  flexWrap: "wrap",
                }}
              >
                {invoiceData?.phone && <span>📞 {invoiceData.phone}</span>}
                {invoiceData?.email && <span>✉️ {invoiceData.email}</span>}
              </div>
            </div>
          </div>

          {/* Right - Title */}
          <div
            style={{
              flex: "0 0 auto",
              textAlign: "right",
              borderLeft: "2px solid #e8e8e8",
              paddingLeft: "20px",
              minWidth: "180px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#003a8c",
                marginBottom: "8px",
                letterSpacing: "1px",
              }}
            >
              PURCHASE INVOICE
            </div>
            {invoiceData?.tag_line && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#999",
                  fontStyle: "italic",
                  marginTop: "4px",
                }}
              >
                {invoiceData.tag_line}
              </div>
            )}
          </div>
        </div>

        {/* GST Exemption Note - match Sale PDF */}
        {invoiceData?.gst_exemption_note && (
          <div
            style={{
              gridColumn: "1/4",
              gridRow: "2",
              textAlign: "center",
              padding: "8px 0",
              fontSize: "11px",
              color: "#666",
              backgroundColor: "#f5f5f5",
              borderTop: "1px solid #e8e8e8",
              borderBottom: "1px solid #e8e8e8",
              marginBottom: "15px",
            }}
          >
            {invoiceData.gst_exemption_note}
          </div>
        )}

        {/* Supplier + Invoice details - match Sale PDF cards */}
        <div
          style={{
            gridColumn: "1/4",
            gridRow: invoiceData?.gst_exemption_note ? "3" : "2",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            marginTop: "5px",
          }}
        >
          {/* Supplier card */}
          <div
            style={{
              flex: "1",
              border: "2px solid #000",
              borderRadius: "4px",
              padding: "8px",
              backgroundColor: "#fafafa",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            }}
          >
            <h4
              style={{
                margin: "0 0 6px 0",
                fontSize: "13px",
                fontWeight: "bold",
                borderBottom: "2px solid #003a8c",
                paddingBottom: "4px",
                color: "#003a8c",
              }}
            >
              Supplier
            </h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <th style={{ padding: "2px 0", textAlign: "left", width: "30%", fontSize: "14px", color: "#666" }}>
                    Name:
                  </th>
                  <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                    {data?.supplier?.name || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th style={{ padding: "2px 0", textAlign: "left", width: "30%", fontSize: "14px", color: "#666" }}>
                    Address:
                  </th>
                  <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                    {data?.supplier?.address || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th style={{ padding: "2px 0", textAlign: "left", width: "30%", fontSize: "14px", color: "#666" }}>
                    Contact:
                  </th>
                  <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                    {data?.supplier?.phone || "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Invoice details card */}
          <div
            style={{
              flex: "1",
              border: "2px solid #000",
              borderRadius: "4px",
              padding: "8px",
              backgroundColor: "#fafafa",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            }}
          >
            <h4
              style={{
                margin: "0 0 6px 0",
                fontSize: "13px",
                fontWeight: "bold",
                borderBottom: "2px solid #003a8c",
                paddingBottom: "4px",
                color: "#003a8c",
              }}
            >
              Invoice Details
            </h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <th style={{ padding: "2px 0", textAlign: "left", width: "35%", fontSize: "14px", color: "#666" }}>
                    Invoice No:
                  </th>
                  <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                    {(data?.prefix || "PINV/25-26/")}
                    {(data?.invoice_number ?? data?.id)}
                  </td>
                </tr>
                <tr>
                  <th style={{ padding: "2px 0", textAlign: "left", width: "35%", fontSize: "14px", color: "#666" }}>
                    Invoice Date:
                  </th>
                  <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                    {moment(data?.date).format("DD-MMM-YYYY")}
                  </td>
                </tr>
                {data?.supplier_memo_no && (
                  <tr>
                    <th style={{ padding: "2px 0", textAlign: "left", width: "35%", fontSize: "14px", color: "#666" }}>
                      Supplier Bill No:
                    </th>
                    <td style={{ padding: "2px 0", textAlign: "left", fontSize: "14px", fontWeight: "500" }}>
                      {data?.supplier_memo_no}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Product details */}
        <div className="box7">
          <table className="table1">
            <thead>
              <th>Sl</th>
              <th>ISBN</th>
              <th>Title</th>
              <th>Author</th>
              <th>Publisher</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Gross</th>
              <th>Dis%</th>
              <th>Amount</th>
            </thead>
            <tbody>
              {data &&
                (sortAlphabetically
                  ? [...data.purchaseInvoiceProduct].sort((a, b) => {
                    const nameA = (a.product?.name || '').toLowerCase();
                    const nameB = (b.product?.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  : data.purchaseInvoiceProduct
                ).map((d, index) => {
                  const itemGross = d.product_quantity * d.product_purchase_price;
                  const itemDiscountAmount = (itemGross * (d.product_purchase_discount || 0)) / 100;
                  const itemAmount = itemGross - itemDiscountAmount;
                  
                  return (
                    <tr key={d.id}>
                      <td>{index + 1}</td>
                      <td>{d.product?.isbn}</td>
                      <td>
                        <p>{d.product?.name}</p>
                      </td>
                      <td>{d.product?.author}</td>
                      <td>{d.product?.book_publisher?.name || d.product?.book_publisher || 'N/A'}</td>
                      <td>₹{d.product_purchase_price}</td>
                      <td>{d.product_quantity}</td>
                      <td>₹ {itemGross.toFixed(2)}</td>
                      <td>{d.product_purchase_discount || 0}%</td>
                      <td>₹ {itemAmount.toFixed(2)}</td>
                    </tr>
                  );
                })}
              {/* Summary rows in the table */}
              <tr style={{ borderTop: '1px solid silver', fontWeight: 'bold' }}>
                <td colSpan="6">Total</td>
                <td>{totalQuantity}</td>
                <td>₹ {grossTotal.toFixed(2)}</td>
                <td>₹ {totalProductDiscountAmount.toFixed(2)}</td>
                <td>₹ {subtotalAfterProductDiscount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Summary Section */}
        <div className="box9" style={{ marginTop: '20px' }}>
          <table className="table2" style={{ width: '100%' }}>
            <tbody>
              {additionalDiscount > 0 && (
                <tr style={{ borderTop: '1px solid #ddd' }}>
                  <th style={{ padding: '8px', textAlign: 'right', width: '70%' }}>
                    Additional Discount (-)
                  </th>
                  <td style={{ padding: '8px', fontWeight: 'bold', textAlign: 'right' }}>
                    ₹ {additionalDiscount.toFixed(2)}
                  </td>
                </tr>
              )}
              {roundOffAmount !== 0 && (
                <tr style={{ borderTop: '1px solid #ddd' }}>
                  <th style={{ padding: '8px', textAlign: 'right', width: '70%' }}>
                    Round Off {roundOffAmount > 0 ? '(+)' : '(-)'}
                  </th>
                  <td style={{ padding: '8px', fontWeight: 'bold', textAlign: 'right' }}>
                    ₹ {Math.abs(roundOffAmount).toFixed(2)}
                  </td>
                </tr>
              )}
              <tr style={{ borderTop: '2px solid #000', fontWeight: 'bold' }}>
                <th style={{ padding: '5px', textAlign: 'right', fontSize: '14px' }}>Grand Total</th>
                <td style={{ padding: '5px', fontWeight: 'bold', textAlign: 'right', fontSize: '14px' }}>
                  ₹ {(subtotalAfterProductDiscount - additionalDiscount - Math.abs(roundOffAmount)).toFixed(2)}
                </td>
              </tr>
              {paidAmount > 0 && (
                <>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Paid Amount</th>
                    <td style={{ padding: '8px', textAlign: 'right', color: 'green' }}>
                      ₹ {paidAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>Due Amount</th>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
                      ₹ {dueAmount.toFixed(2)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Amount in Words */}
        <div
          className="box16"
          style={{
            gridColumn: "1/4",
            gridRow: "6",
            marginTop: "16px",
            padding: "8px 10px",
          }}
        >
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
            <b>Amount In Words: </b>
            Rs. {camelize(number2words(Math.round(subtotalAfterProductDiscount - additionalDiscount - Math.abs(roundOffAmount))))} Only.
          </p>
          {data?.note && (
            <p style={{ fontSize: '12px', marginTop: '8px', marginBottom: 0 }}>
              <b>Notes: </b>
              {data.note}
            </p>
          )}
        </div>

        {/* Footer Section (pin to end like Sale) */}
        <div
          style={{
            gridColumn: "1/4",
            gridRow: "7",
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
            padding: "8px 10px",
            marginTop: "8px",
            alignItems: "flex-start",
          }}
        >
          {/* Left Column - Terms & Conditions */}
          <div style={{ width: '50%', textAlign: 'left' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
              <b>Terms & Conditions: </b>
            </p>
            <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
              {(invoiceData?.terms || '')
                .split(/\r\n|\n|\r/)
                .filter((line) => line.trim() !== '')
                .map((item, key) => (
                  <Fragment key={key}>
                    {item}
                    <br />
                  </Fragment>
                ))}
            </div>
          </div>

          {/* Right Column - Bank Details */}
          <div style={{ width: '50%', textAlign: 'right' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
              <b>Pay To: </b>
            </p>
            <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
              Bank Name: {invoiceData?.bank_name || 'N/A'}
              <br />
              Account Name: {invoiceData?.account_name || 'N/A'}
              <br />
              Account Number: {invoiceData?.account_number || 'N/A'}
              <br />
              IFSC: {invoiceData?.ifsc || 'N/A'}
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div
          style={{
            gridColumn: "1/4",
            gridRow: "8",
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
            padding: "0 10px",
            marginTop: "10px",
          }}
        >
          <div style={{ width: '50%', textAlign: 'left' }}>
            <hr style={{ margin: '0 0 4px 0' }} />
            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Received By</div>
          </div>
          <div style={{ width: '50%', textAlign: 'right' }}>
            <hr style={{ margin: '0 0 4px 0' }} />
            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Authorized By</div>
          </div>
        </div>

        {/* Footer line */}
        <div
          style={{
            gridColumn: "1/4",
            gridRow: "9",
            textAlign: "center",
            padding: "6px 10px",
            borderTop: "1px solid #000",
            marginTop: "10px",
          }}
        >
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.2' }}>
            Powered by SOFTOMATION | Contact: +91-9999782899
          </p>
        </div>

        <div className="print-page-number" />
      </div>
    </Fragment>
  );
});

const PurchaseInvoice = ({ data, autoShow = false, onClose, autoPrintOnce = false }) => {
  const componentRef = useRef();
  const hasAutoPrinted = useRef(false);
  const [showPdf, setShowPDF] = useState(autoShow);
  const [sortAlphabetically, setSortAlphabetically] = useState(true);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${data?.prefix || ""}${data?.invoice_number || "purchase-invoice"}`,
  });

  const handleDownloadPdf = async () => {
    const el = componentRef.current;
    if (!el) return;

    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: 1000,
      width: 1000,
      onclone: (clonedDoc) => {
        // Don't bake the on-page meta row into the PDF image; we draw it separately on pages 2+
        clonedDoc.querySelectorAll(".invoice-top-meta").forEach((n) => {
          n.style.display = "none";
        });
        const wrap = clonedDoc.querySelector(".wrapper");
        if (wrap) {
          wrap.style.width = "1000px";
          wrap.style.maxWidth = "1000px";
          wrap.style.margin = "0 auto";
        }
      },
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Set margins matching the print preview (e.g., ~10-20pt)
    const marginX = 20;
    const marginY = 20;

    const imgWidth = pageWidth - marginX * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = marginY;

    pdf.addImage(imgData, "PNG", marginX, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - marginY * 2;

    while (heightLeft > 0) {
      position = position - pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", marginX, position + marginY, imgWidth, imgHeight);
      heightLeft -= pageHeight - marginY * 2;
    }

    const totalPages = pdf.getNumberOfPages();

    // Add header on pages 2..N (NOT on first page)
    const invNo = `${data?.prefix || ""}${data?.invoice_number ?? data?.id ?? ""}`;
    const headerLeft = `Invoice No: ${invNo}`;
    const headerRight = `Invoice Date: ${moment(data?.date).format("DD-MMM-YYYY")}`;

    // Draw header/page numbers as an overlay pass so it never gets covered by the image.
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    const headerY = 36; // comfortably inside visible area
    const headerBgHeight = 52;
    const pageNoY = pageHeight - 14;
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);

      // Page number: "Page X of Y"
      pdf.text(`Page ${p} of ${totalPages}`, pageWidth - marginX, pageNoY, { align: "right" });

      if (p === 1) continue;

      // Header (pages 2+ only): white strip + left/right text
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, headerBgHeight, "F");
      pdf.text(headerLeft, marginX, headerY);
      pdf.text(headerRight, pageWidth - marginX, headerY, { align: "right" });
    }

    const fileName = `${data?.prefix || ""}${data?.invoice_number || "purchase-invoice"}.pdf`;
    pdf.save(fileName);
  };

  const [invoiceData, setInvoiceData] = useState(null);
  useEffect(() => {
    getSetting().then((data) => setInvoiceData(data.result));
  }, []);

  // Auto-show PDF on mobile
  useEffect(() => {
    if (autoShow && invoiceData) {
      setShowPDF(true);
    }
  }, [autoShow, invoiceData]);

  const showPrint = () => {
    setShowPDF(true);
  };

  const hideShowPrint = () => {
    setShowPDF(false);
    onClose?.();
  };

  // When opened for "Print PDF" from list: trigger print once after content is ready
  useEffect(() => {
    if (!autoPrintOnce || !showPdf || !data || !invoiceData || hasAutoPrinted.current) return;
    hasAutoPrinted.current = true;
    const t = setTimeout(() => {
      handlePrint();
      hideShowPrint();
    }, 400);
    return () => clearTimeout(t);
  }, [autoPrintOnce, showPdf, data, invoiceData]);

  useEffect(() => {
    if (!showPdf) return;
    const onEsc = (e) => {
      if (e.key === "Escape") hideShowPrint();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showPdf]);

  // If autoPrintOnce is true, we visually hide the modal so the user doesn't see it flash
  const popupStyle = autoPrintOnce ? { opacity: 0, pointerEvents: 'none' } : {};

  return (
    <div>
      {showPdf && (
        <div className="popUp" style={popupStyle}>
          <div className="invoice-preview">
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 8, gap: 8 }}>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={hideShowPrint}
                aria-label="Close"
                style={{ marginLeft: "auto", color: "#ff4d4f" }}
              >
                Close
              </Button>
            </div>
            {/* Mobile Back Button */}
            <div className="d-lg-none mb-3">
              <Button 
                type="default" 
                onClick={() => hideShowPrint()}
                style={{ marginBottom: '16px' }}
              >
                ← Back to Details
              </Button>
            </div>
            
            <PrintToPdf
              ref={componentRef}
              data={data}
              invoiceData={invoiceData}
              sortAlphabetically={sortAlphabetically}
            />
            {invoiceData && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
                <Checkbox
                  checked={sortAlphabetically}
                  onChange={(e) => setSortAlphabetically(e.target.checked)}
                >
                  Sort Titles A-Z
                </Checkbox>
                <Button
                  type="primary"
                  shape="round"
                  onClick={() => {
                    handlePrint();
                    hideShowPrint();
                  }}
                >
                  Print PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {!showPdf && invoiceData && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button type="primary" shape="round" onClick={showPrint}>
            Preview
          </Button>
          <Button type="default" shape="round" onClick={handleDownloadPdf}>
            Download PDF
          </Button>
          <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', zIndex: -1000, visibility: 'hidden' }}>
            <PrintToPdf
              ref={componentRef}
              data={data}
              invoiceData={invoiceData}
              sortAlphabetically={sortAlphabetically}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseInvoice;
